import { useState, useEffect, useCallback, useMemo } from 'react';
import { getUpcomingHolidays, getDaysUntil, initializeHolidays, type Holiday } from '../utils/holidays';

export interface Notification {
  id: string;
  type: 'holiday' | 'custom';
  title: string;
  message: string;
  market?: 'canada' | 'usa';
  date: string; // ISO date string
  dismissedAt?: string; // ISO timestamp when dismissed
}

const STORAGE_KEY = 'ts-shipment-tracker-dismissed-notifications';
const DAYS_AHEAD = 7; // Show holidays 7 days in advance

// Load dismissed notifications from localStorage
function loadDismissed(): Record<string, string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load dismissed notifications:', e);
  }
  return {};
}

// Save dismissed notifications to localStorage
function saveDismissed(dismissed: Record<string, string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
  } catch (e) {
    console.error('Failed to save dismissed notifications:', e);
  }
}

// Generate a unique ID for a holiday notification
function getHolidayNotificationId(holiday: Holiday): string {
  return `holiday-${holiday.market}-${holiday.date}`;
}

// Convert a holiday to a notification
function holidayToNotification(holiday: Holiday): Notification {
  const daysUntil = getDaysUntil(holiday.date);
  const marketLabel = holiday.market === 'canada' ? '🇨🇦 Canada' : '🇺🇸 USA';

  let message: string;
  if (daysUntil === 0) {
    message = `${marketLabel} holiday today - no carrier pickups`;
  } else if (daysUntil === 1) {
    message = `${marketLabel} holiday tomorrow - plan accordingly`;
  } else {
    message = `${marketLabel} holiday in ${daysUntil} days`;
  }

  return {
    id: getHolidayNotificationId(holiday),
    type: 'holiday',
    title: holiday.name,
    message,
    market: holiday.market,
    date: holiday.date,
  };
}

export function useNotifications() {
  const [dismissed, setDismissed] = useState<Record<string, string>>(() => loadDismissed());
  const [holidaysLoaded, setHolidaysLoaded] = useState(false);

  // Initialize holidays from API on mount
  useEffect(() => {
    initializeHolidays().then(() => {
      setHolidaysLoaded(true);
    });
  }, []);

  // Get upcoming holiday notifications
  const holidayNotifications = useMemo(() => {
    // Re-compute when holidays are loaded
    void holidaysLoaded; // Dependency trigger
    const holidays = getUpcomingHolidays(DAYS_AHEAD);
    return holidays.map(holidayToNotification);
  }, [holidaysLoaded]);

  // Filter out dismissed notifications (unless they were dismissed more than 24 hours ago for "today" alerts)
  const activeNotifications = useMemo(() => {
    const now = new Date();

    return holidayNotifications.filter((n) => {
      const dismissedAt = dismissed[n.id];
      if (!dismissedAt) return true;

      // For "today" notifications, show again if dismissed more than 24 hours ago
      const daysUntil = getDaysUntil(n.date);
      if (daysUntil === 0) {
        const dismissedTime = new Date(dismissedAt);
        const hoursSinceDismissed = (now.getTime() - dismissedTime.getTime()) / (1000 * 60 * 60);
        return hoursSinceDismissed > 24;
      }

      return false;
    });
  }, [holidayNotifications, dismissed]);

  // Dismiss a notification
  const dismissNotification = useCallback((notificationId: string) => {
    setDismissed((prev) => {
      const updated = {
        ...prev,
        [notificationId]: new Date().toISOString(),
      };
      saveDismissed(updated);
      return updated;
    });
  }, []);

  // Dismiss all notifications
  const dismissAll = useCallback(() => {
    setDismissed((prev) => {
      const updated = { ...prev };
      for (const n of activeNotifications) {
        updated[n.id] = new Date().toISOString();
      }
      saveDismissed(updated);
      return updated;
    });
  }, [activeNotifications]);

  // Clean up old dismissed entries (older than 30 days)
  useEffect(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const cleaned: Record<string, string> = {};
    let hasChanges = false;

    for (const [id, timestamp] of Object.entries(dismissed)) {
      const dismissedAt = new Date(timestamp);
      if (dismissedAt > thirtyDaysAgo) {
        cleaned[id] = timestamp;
      } else {
        hasChanges = true;
      }
    }

    if (hasChanges) {
      setDismissed(cleaned);
      saveDismissed(cleaned);
    }
  }, []); // Run once on mount

  return {
    notifications: activeNotifications,
    allNotifications: holidayNotifications,
    dismissNotification,
    dismissAll,
    hasNotifications: activeNotifications.length > 0,
    notificationCount: activeNotifications.length,
  };
}
